/*
 * Copyright 2026 Sony Corporation
 */

import type { DeviceStateResponse, CreativeLook } from '@ips-device-verse/twin-service/schema';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { createThrottle } from '../scene/throttle';
import { resolveLook, type LookView } from '../creative-look/resolve';
import { CREATIVE_LOOK_FIELDS, type CreativeLookField } from '../creative-look/fields';
import { useAsync } from './use-async';

export type CreativeLookValue = CreativeLook[CreativeLookField];

export interface UseCreativeLookOptions {
  worldId: string;
  /** Absent when nothing is selected; the hook then reads nothing. */
  deviceId: string | undefined;
  /** Pushed over the socket; newer than anything this hook fetched. */
  live?: DeviceStateResponse;
}

export interface UseCreativeLookResult extends LookView {
  /** True once any gateway has reported this device at all. */
  reporting: boolean;
  /** Whether a PTP client is attached to the device. Not the gateway link. */
  connected: boolean | undefined;
  /** Friendly name of that client, kept after it detaches. */
  client: string | undefined;
  loading: boolean;
  loadError: Error | undefined;
  /** Why a field's write failed, keyed by field. */
  errors: Partial<Record<CreativeLookField, string>>;
  set: (field: CreativeLookField, value: CreativeLookValue) => void;
  reload: () => void;
}

/** A slider drag fires continuously; one write per fifth of a second is plenty. */
const WRITE_INTERVAL_MS = 200;

/**
 * How long an edit may go unanswered before it is called refused.
 *
 * A device that silently keeps its old value is indistinguishable from one that
 * has not answered yet, so the only honest end to that wait is a clock. Long
 * enough to cover a gateway's debounce and a round trip, short enough that a
 * control never looks stuck.
 */
const APPLY_TIMEOUT_MS = 5_000;

interface Edits {
  /** Edits belong to a device, so selecting another discards them by derivation. */
  deviceId: string | undefined;
  pending: Partial<CreativeLook>;
  rejected: Partial<Record<CreativeLookField, string>>;
  /** What the device reported for each field when it was edited. */
  baseline: Partial<CreativeLook>;
  timedOut: Partial<Record<CreativeLookField, true>>;
}

const NO_EDITS: Edits = { deviceId: undefined, pending: {}, rejected: {}, baseline: {}, timedOut: {} };

const omit = <T extends object>(source: T, key: keyof T): T => {
  const { [key]: _removed, ...rest } = source;
  return rest as T;
};

/** A computed key widens to a string index, and the write schema is strict. */
const single = (field: CreativeLookField, value: CreativeLookValue): CreativeLook =>
  ({ [field]: value }) as CreativeLook;

/**
 * The nine Creative Look controls: what to show, and what an edit does.
 *
 * Edits are optimistic and held per field, so a slow write for one slider cannot
 * stamp over another, and an echo of the user's own earlier value cannot fight a
 * drag in progress (devplan 4.2). A write that fails drops just that field's
 * pending value, which snaps the control back to what the device last confirmed.
 */
export const useCreativeLook = ({ worldId, deviceId, live }: UseCreativeLookOptions): UseCreativeLookResult => {
  const { api } = useAuth();

  const {
    data: fetched,
    error: loadError,
    loading,
    reload,
  } = useAsync<DeviceStateResponse | undefined>(
    async () => (deviceId ? api.getDeviceState(worldId, deviceId) : undefined),
    [api, worldId, deviceId]
  );

  // The socket carries whatever the device last confirmed, so it outranks the
  // snapshot taken when the panel opened.
  const state = live ?? fetched;

  const [edits, setEdits] = useState<Edits>(NO_EDITS);
  const active = edits.deviceId === deviceId ? edits : NO_EDITS;

  // Read when an edit is made, so the reply can be told from the request's own
  // echo: writing `desired` bumps the document version but not this.
  const reportedRef = useRef(state?.state.reported?.creativeLook);
  useEffect(() => {
    reportedRef.current = state?.state.reported?.creativeLook;
  });

  const timers = useRef<Partial<Record<CreativeLookField, number>>>({});
  useEffect(() => {
    const armed = timers.current;
    return () => {
      for (const handle of Object.values(armed)) {
        window.clearTimeout(handle);
      }
    };
  }, []);

  const write = useCallback(
    (field: CreativeLookField, value: CreativeLookValue) => {
      if (!deviceId) {
        return;
      }
      api.writeDeviceState(worldId, deviceId, { creative_look: single(field, value) }).catch((error: Error) => {
        setEdits((previous) =>
          previous.deviceId !== deviceId
            ? previous
            : {
                ...previous,
                pending: omit(previous.pending, field),
                rejected: { ...previous.rejected, [field]: error.message },
              }
        );
      });
    },
    [api, worldId, deviceId]
  );

  // Rebuilt only when the device it writes to changes, so a drag is never
  // interrupted; the old one is flushed rather than cancelled, because the value
  // a drag ends on is the one that must not be dropped.
  const throttle = useMemo(
    () =>
      createThrottle<CreativeLookValue>({
        intervalMs: WRITE_INTERVAL_MS,
        // The throttle keys on strings; here every key is a field name.
        send: (field, value) => write(field as CreativeLookField, value),
      }),
    [write]
  );
  useEffect(
    () => () => {
      for (const field of CREATIVE_LOOK_FIELDS) {
        throttle.flush(field);
      }
    },
    [throttle]
  );

  const set = useCallback(
    (field: CreativeLookField, value: CreativeLookValue) => {
      setEdits((previous) => {
        const base = previous.deviceId === deviceId ? previous : NO_EDITS;
        return {
          deviceId,
          pending: { ...base.pending, [field]: value },
          rejected: omit(base.rejected, field),
          // Only the first edit of a run sets the baseline: dragging a slider
          // must be measured against where the device was before the drag, not
          // against the value it reported halfway through it.
          baseline: field in base.pending ? base.baseline : { ...base.baseline, [field]: reportedRef.current?.[field] },
          timedOut: omit(base.timedOut, field),
        };
      });
      throttle.push(field, value);

      window.clearTimeout(timers.current[field]);
      timers.current[field] = window.setTimeout(() => {
        setEdits((previous) =>
          previous.deviceId === deviceId ? { ...previous, timedOut: { ...previous.timedOut, [field]: true } } : previous
        );
      }, APPLY_TIMEOUT_MS);
    },
    [deviceId, throttle]
  );

  return {
    ...resolveLook({
      state,
      pending: active.pending,
      rejected: active.rejected,
      baseline: active.baseline,
      timedOut: active.timedOut,
    }),
    reporting: state?.state.reported !== undefined,
    connected: state?.state.reported?.session.connected,
    client: state?.state.reported?.session.client,
    loading,
    loadError,
    errors: active.rejected,
    set,
    reload,
  };
};
